import React from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import {
  useDriversWithWorstPerformance,
  HarshDrivingReportsHookParams,
} from "../hooks/useHarshDrivingReportsHooks";
import type { DriverPerformanceItemDto } from "../../../common/data/service-proxies";
import { PerformersTable } from "../common/PerformersTable";
import "../fuel-economy-report/FuelEconomyReport.css";

export interface HarshReportsWorstPerformersProps
  extends HarshDrivingReportsHookParams {}

export const HarshReportsWorstPerformers: React.FC<HarshReportsWorstPerformersProps> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const { data, loading: isLoading, error } = useDriversWithWorstPerformance({
    fromDate,
    toDate,
    periodType,
  });
  const isError = !!error;
  const rows: DriverPerformanceItemDto[] = data?.data ?? [];

  const renderValueCell = (row: DriverPerformanceItemDto) => (
    <Typography variant="body2">
      {row.numberOfHarshEvents != null
        ? `${row.numberOfHarshEvents} ${translate(
            "harshDrivingReport.harshEventsCountUnit",
            {
              _: "harsh events",
            }
          )}`
        : "-"}
    </Typography>
  );

  return (
    <Card className="widget" style={{ height: "100%", width: "100%" }}>
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("harshDrivingReport.worstPerformers", {
            _: "Worst performers",
          })}
        </div>
      </div>
      <CardContent
        style={{
          padding: 16,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !rows.length ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : (
          <PerformersTable<DriverPerformanceItemDto>
            rows={rows}
            valueColumnHeader="harshDrivingReport.harshEvents"
            renderValueCell={renderValueCell}
            getRowKey={(row, index) =>
              `${row.vehicleName ?? ""}-${row.driverFullName ?? ""}-${index}`
            }
            isReverse={true}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default HarshReportsWorstPerformers;
